import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Button } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { RelatorioContainer } from '@/shared/components/relatorios/components/relatorio-container/relatorio-container';
import { RelatorioMedicoBase } from '@/features/registro/medicos/relatorios/relatorio-medico.base';
import { RelatorioJobFacade } from '@/shared/components/relatorios/relatorio-job-base';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { CardMedicoSelecionado } from '@/features/registro/medicos/relatorios/components/card-medico-selecionado/card-medico-selecionado';
import { OficioConfirmacaoCancelamentoIncSecundariaMedicoService } from '@/core/api/sicom-api/registro/medico/relatorios/oficio-confirmacao-cancelamento-inc-secundaria-medico/oficio-confirmacao-cancelamento-inc-secundaria-medico.service';
import {
    ReportOficioConfirmacaoCancelamentoIncSecundariaMedicoRequest,
    ReportOficioConfirmacaoCancelamentoIncSecundariaMedicoResponse
} from '@/core/api/sicom-api/registro/medico/relatorios/oficio-confirmacao-cancelamento-inc-secundaria-medico/oficio-confirmacao-cancelamento-inc-secundaria-medico.model';
import { OficioConfirmacaoCancelamentoIncSecundariaMedicoJobService } from '@/core/services/oficio-confirmacao-cancelamento-inc-secundaria-medico-job.service';
import { CanDirective } from '@/core/permissions/can.directive';

type Req = ReportOficioConfirmacaoCancelamentoIncSecundariaMedicoRequest;
type Resp = ReportOficioConfirmacaoCancelamentoIncSecundariaMedicoResponse;

interface OficioConfirmacaoCancelamentoIncSecundariaMedicoFormControls {
    conselhoDestino: FormControl<string | null>;
    presidente: FormControl<string | null>;
    emissor: FormControl<string | null>;
}

/**
 * Relatório PDF assíncrono: Ofício de Confirmação de Cancelamento de Inscrição Secundária
 * (Médico), comunicando ao CRM de inscrição principal o cancelamento da inscrição
 * secundária do médico neste Conselho. Identificação do médico via nuCrm/sgUf
 * (PessoaService). "Conselho de destino/principal", "presidente" e "emissor" são
 * opcionais: se não informados, o backend tenta resolver o conselho automaticamente
 * e os demais campos ficam em branco no documento.
 *
 * Herda `RelatorioMedicoBase`: toda a orquestração de export/polling/download/erro e o card
 * "médico selecionado + Alterar" vêm da base; aqui só montamos o request (médico + campos
 * opcionais do form) e delegamos o HTTP ao service.
 */
@Component({
    selector: 'app-oficio-confirmacao-cancelamento-inc-secundaria-medico',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [AsyncPipe, Button, ReactiveFormsModule, InputTextModule, TooltipModule, RelatorioContainer, Pesquisa, CardMedicoSelecionado, CanDirective],
    templateUrl: './oficio-confirmacao-cancelamento-inc-secundaria-medico.html',
    styleUrl: './oficio-confirmacao-cancelamento-inc-secundaria-medico.scss'
})
export class ReportOficioConfirmacaoCancelamentoIncSecundariaMedicoComponent extends RelatorioMedicoBase<Req, Resp> {
    private readonly formBuilder = inject(FormBuilder);
    private readonly oficioService = inject(OficioConfirmacaoCancelamentoIncSecundariaMedicoService);
    readonly oficioJobService = inject(OficioConfirmacaoCancelamentoIncSecundariaMedicoJobService);

    /** Stream do job para o `app-relatorio-container` (preview/PDF) no template. */
    readonly job$ = this.oficioJobService.job$;

    form: FormGroup<OficioConfirmacaoCancelamentoIncSecundariaMedicoFormControls> = this.formBuilder.group<OficioConfirmacaoCancelamentoIncSecundariaMedicoFormControls>({
        conselhoDestino: new FormControl<string | null>(''),
        presidente: new FormControl<string | null>(''),
        emissor: new FormControl<string | null>('')
    });

    protected get jobFacade(): RelatorioJobFacade {
        return this.oficioJobService;
    }

    /**
     * Monta o request a partir do médico selecionado (nuCrm/sgUf) + campos opcionais do form;
     * retorna null (e avisa) se faltar CRM/UF.
     */
    protected buildRequest(): Req | null {
        const medico = this.resolverIdentificacaoMedico('Selecione um médico (CRM/UF) antes de gerar o ofício.');
        if (!medico) return null;
        const { nuCrm, sgUf } = medico;

        const { conselhoDestino, presidente, emissor } = this.form.getRawValue();

        return {
            nuCrm,
            sgUf,
            ...(conselhoDestino?.trim() ? { conselhoDestino: conselhoDestino.trim() } : {}),
            ...(presidente?.trim() ? { presidente: presidente.trim() } : {}),
            ...(emissor?.trim() ? { emissor: emissor.trim() } : {})
        };
    }

    protected exportar(request: Req) {
        return this.oficioService.exportarPdf(request);
    }

    limparFormulario(): void {
        this.form.reset({ conselhoDestino: '', presidente: '', emissor: '' });
        this.limparJob();
    }
}
